import {supabase} from '../config/supabase';
import {roomService} from './roomService';

class SocketService {
  constructor() {
    this.socket = null;
    this.isConnected = false;
    this.listeners = new Map();
    this.currentRoom = null;
    this.deviceId = localStorage.getItem('clipshare-device-id') || `Device-${Math.floor(Math.random() * 10000)}`;
    this.deviceIp = null;

    // In-memory clipboard items storage for real-time sharing
    this.clipboardItems = new Map(); // Map of rooms to array of clipboard items

    // Connected devices tracking
    this.connectedDevices = new Map(); // Map of deviceId to device info

    // Save device ID to localStorage for persistence
    if (!localStorage.getItem('clipshare-device-id')) {
      localStorage.setItem('clipshare-device-id', this.deviceId);
    }

    // Fetch IP address
    this.fetchIpAddress();

    // Room expiration check interval
    this.expirationCheckInterval = null;
  }

  // Fetch IP address using a public API
  async fetchIpAddress() {
    try {
      const response = await fetch('https://api.ipify.org?format=json');
      const data = await response.json();
      this.deviceIp = data.ip;
      console.log(`[Socket] Device IP: ${this.deviceIp}`);
    } catch (error) {
      console.error('[Socket] Error fetching IP address:', error);
      this.deviceIp = 'Unknown';
    }
  }

  connect(serverUrl = 'ws://localhost:5000') {
    if (this.socket) {
      this.disconnect();
    }

    // For demo purposes, we'll use Supabase Realtime for communication
    this.socket = {
      emit: (event, data, callback) => {
        console.log(`[Socket] Emitting: ${event}`, data);

        // Handle special events
        if (event === 'join_room') {
          this.currentRoom = data.room;
          this.setupRealtimeSubscription();
          this.startExpirationCheck();
        } else if (event === 'add_clipboard_item') {
          this.addClipboardItem(data.room, data.item);
        } else if (event === 'clear_clipboard_items') {
          this.clearClipboardItems(data.room);
        }

        if (callback) callback();
      },
      on: (event, handler) => {
        if (!this.listeners.has(event)) {
          this.listeners.set(event, []);
        }
        this.listeners.get(event).push(handler);
        console.log(`[Socket] Added listener for: ${event}`);
      },
      off: (event, handler) => {
        const handlers = this.listeners.get(event);
        if (handlers) {
          const index = handlers.indexOf(handler);
          if (index > -1) {
            handlers.splice(index, 1);
          }
        }
      },
      disconnect: () => {
        this.isConnected = false;
        this.emit('disconnect');
      }
    };

    this.isConnected = true;

    // Simulate connection events
    setTimeout(() => {
      this.emit('connect');
    }, 100);

    return this.socket;
  }

  // Start checking for room expiration
  startExpirationCheck() {
    // Clear any existing interval
    if (this.expirationCheckInterval) {
      clearInterval(this.expirationCheckInterval);
    }

    // Check room expiration every 30 seconds
    this.expirationCheckInterval = setInterval(async () => {
      if (this.currentRoom) {
        try {
          // Use the correct function name: getRoomExpiration instead of checkRoomExpiration
          const {success, isExpired} = await roomService.getRoomExpiration(this.currentRoom);
          
          if (success && isExpired) {
            console.log(`[Socket] Room ${this.currentRoom} has expired`);
            this.emit('room_expired');
            this.disconnect();
          }
        } catch (error) {
          console.error('[Socket] Error checking room expiration:', error);
        }
      }
    }, 30 * 1000); // Check every 30 seconds
  }

  // Set up Supabase realtime subscription for real-time communication
  async setupRealtimeSubscription() {
    if (!this.currentRoom) {
      console.error('[Socket] Cannot setup subscription: No current room');
      return;
    }

    console.log(`[Socket] Setting up realtime subscription for room: ${this.currentRoom}`);

    try {
      // Create a channel for this room
      const channelName = `room-${this.currentRoom}`;
      console.log(`[Socket] Creating channel: ${channelName}`);

      // Create Supabase Broadcast channel for real-time communication
      const channel = supabase.channel(channelName, {
        config: {
          broadcast: { self: true }
        }
      });

      // Listen for clipboard events
      channel
        .on('broadcast', { event: 'clipboard_item' }, (payload) => {
          console.log('[Socket] Received clipboard item broadcast:', payload);
          if (payload.payload && payload.payload.item) {
            this.handleReceivedClipboardItem(payload.payload.item);
          }
        })
        .on('broadcast', { event: 'clipboard_sync' }, (payload) => {
          console.log('[Socket] Received clipboard sync broadcast:', payload);
          if (payload.payload && payload.payload.items) {
            this.emit('clipboard_sync', payload.payload.items);
          }
        })
        .on('broadcast', { event: 'clear_clipboard' }, (payload) => {
          console.log('[Socket] Received clear clipboard broadcast:', payload);
          if (payload.payload && payload.payload.room === this.currentRoom) {
            // Clear the local items
            if (this.clipboardItems.has(this.currentRoom)) {
              this.clipboardItems.set(this.currentRoom, []);
            }
            // Notify listeners
            this.emit('clipboard_sync', []);
          }
        })
        .on('broadcast', { event: 'user_joined' }, (payload) => {
          console.log('[Socket] User joined:', payload);
          if (payload.payload) {
            // Add to connected devices
            const deviceInfo = {
              deviceId: payload.payload.deviceId,
              ip: payload.payload.ip || 'Unknown',
              joinedAt: payload.payload.timestamp || new Date().toISOString(),
              isCurrent: payload.payload.deviceId === this.deviceId
            };

            this.connectedDevices.set(deviceInfo.deviceId, deviceInfo);

            // Emit updated device list
            this.emitConnectedDevices();

            // Broadcast current items to the new user if it's not us
            if (payload.payload.deviceId !== this.deviceId) {
              const items = this.clipboardItems.get(this.currentRoom) || [];
              channel.send({
                type: 'broadcast',
                event: 'clipboard_sync',
                payload: { items }
              });

              // Also send our device info to sync
              channel.send({
                type: 'broadcast',
                event: 'device_info',
                payload: {
                  deviceId: this.deviceId,
                  ip: this.deviceIp,
                  timestamp: new Date().toISOString()
                }
              });
            }
          }
        })
        .on('broadcast', { event: 'device_info' }, (payload) => {
          console.log('[Socket] Device info received:', payload);
          if (payload.payload && payload.payload.deviceId) {
            // Update device info
            const deviceInfo = {
              deviceId: payload.payload.deviceId,
              ip: payload.payload.ip || 'Unknown',
              joinedAt: payload.payload.timestamp || new Date().toISOString(),
              isCurrent: payload.payload.deviceId === this.deviceId
            };

            this.connectedDevices.set(deviceInfo.deviceId, deviceInfo);

            // Emit updated device list
            this.emitConnectedDevices();
          }
        })
        .on('broadcast', { event: 'user_left' }, (payload) => {
          console.log('[Socket] User left:', payload);
          if (payload.payload && payload.payload.deviceId) {
            // Remove from connected devices
            this.connectedDevices.delete(payload.payload.deviceId);

            // Emit updated device list
            this.emitConnectedDevices();

            // Could update user count or show notification
            this.emit('user_left', payload.payload);
          }
        })
        .on('broadcast', { event: 'room_expired' }, () => {
          console.log('[Socket] Room expired broadcast received');
          this.emit('room_expired');
        })
        .subscribe((status, err) => {
          if (status === 'SUBSCRIBED') {
            console.log(`[Socket] Successfully subscribed to channel ${channelName}`);

            // Add ourselves to the connected devices list
            const deviceInfo = {
              deviceId: this.deviceId,
              ip: this.deviceIp,
              joinedAt: new Date().toISOString(),
              isCurrent: true
            };
            this.connectedDevices.set(this.deviceId, deviceInfo);

            // Announce this device joined
            channel.send({
              type: 'broadcast',
              event: 'user_joined',
              payload: {
                deviceId: this.deviceId,
                ip: this.deviceIp,
                timestamp: new Date().toISOString()
              }
            });

            // Send initial items to new member
            const existingItems = this.clipboardItems.get(this.currentRoom) || [];
            this.emit('clipboard_sync', existingItems);

            // Emit connected devices
            this.emitConnectedDevices();

            // Also broadcast current items to sync everyone
            setTimeout(() => {
              channel.send({
                type: 'broadcast',
                event: 'clipboard_sync',
                payload: { items: existingItems }
              });
            }, 500);

          } else if (status === 'CHANNEL_ERROR') {
            console.error(`[Socket] Error subscribing to channel:`, err);
          } else {
            console.log(`[Socket] Channel status: ${status}`);
          }
        });

      // Store the channel for later cleanup
      this.currentChannel = channel;

      console.log(`[Socket] Realtime subscription setup complete for room ${this.currentRoom}`);
    } catch (error) {
      console.error('[Socket] Error setting up realtime subscription:', error);
    }
  }

  // Emit the current list of connected devices
  emitConnectedDevices() {
    const devices = Array.from(this.connectedDevices.values());
    console.log('[Socket] Connected devices:', devices);
    this.emit('connected_devices', devices);
  }

  // Handle a received clipboard item
  handleReceivedClipboardItem(item) {
    if (!item || !item.room_name || item.room_name !== this.currentRoom) {
      return;
    }

    // Store in memory
    if (!this.clipboardItems.has(this.currentRoom)) {
      this.clipboardItems.set(this.currentRoom, []);
    }

    const roomItems = this.clipboardItems.get(this.currentRoom);

    // Check for duplicates
    const isDuplicate = roomItems.some(existing => 
      existing.id === item.id || 
      (existing.content === item.content && 
       existing.device_name === item.device_name && 
       Math.abs(new Date(existing.timestamp) - new Date(item.timestamp)) < 1000)
    );

    if (!isDuplicate) {
      // Add to the beginning of the array
      roomItems.unshift(item);
      this.clipboardItems.set(this.currentRoom, roomItems);

      // Notify listeners
      this.emit('new_clipboard_item', item);
    }
  }

  // Add a clipboard item
  async addClipboardItem(roomName, item) {
    console.log('[Socket] Adding clipboard item:', item);

    try {
      // Update room activity (but not expiration time)
      await roomService.updateRoomActivity(roomName);

      const newItem = {
        id: `item-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`,
        room_name: roomName,
        content: item.content,
        device_name: item.deviceName || this.deviceId,
        timestamp: item.timestamp || new Date().toISOString()
      };

      console.log('[Socket] Broadcasting new clipboard item:', newItem);

      // Store in memory
      if (!this.clipboardItems.has(roomName)) {
        this.clipboardItems.set(roomName, []);
      }

      const roomItems = this.clipboardItems.get(roomName);
      roomItems.unshift(newItem);
      this.clipboardItems.set(roomName, roomItems);

      // Broadcast to others
      if (this.currentChannel) {
        this.currentChannel.send({
          type: 'broadcast',
          event: 'clipboard_item',
          payload: { item: newItem }
        });
      }

      return newItem;
    } catch (error) {
      console.error('[Socket] Error adding clipboard item:', error);
      throw error;
    }
  }

  // Clear all clipboard items for a room
  async clearClipboardItems(roomName) {
    console.log(`[Socket] Clearing clipboard items for room: ${roomName}`);

    try {
      // Update room activity but not expiration
      await roomService.updateRoomActivity(roomName);

      // Clear items in memory
      this.clipboardItems.set(roomName, []);

      // Broadcast to all devices
      if (this.currentChannel) {
        this.currentChannel.send({
          type: 'broadcast',
          event: 'clear_clipboard',
          payload: { room: roomName }
        });
      }

      // Emit local event
      this.emit('clipboard_sync', []);

      return true;
    } catch (error) {
      console.error('[Socket] Error clearing clipboard items:', error);
      throw error;
    }
  }

  disconnect() {
    console.log('[Socket] Disconnecting...');

    try {
      // Broadcast that this user is leaving before disconnecting
      if (this.currentChannel && this.currentRoom) {
        this.currentChannel.send({
          type: 'broadcast',
          event: 'user_left',
          payload: {
            deviceId: this.deviceId,
            timestamp: new Date().toISOString()
          }
        });
      }

      if (this.socket) {
        this.socket.disconnect();
        this.socket = null;
        this.isConnected = false;
        this.listeners.clear();
      }

      // Clean up Supabase channel
      if (this.currentChannel) {
        this.currentChannel.unsubscribe();
        this.currentChannel = null;
      }

      // Clear expiration check interval
      if (this.expirationCheckInterval) {
        clearInterval(this.expirationCheckInterval);
        this.expirationCheckInterval = null;
      }

      this.currentRoom = null;
      this.connectedDevices.clear();
    } catch (error) {
      console.error('[Socket] Error during disconnect:', error);
    }
  }

  emit(event, data) {
    console.log(`[Socket] Emitting event: ${event}`, data);
    const handlers = this.listeners.get(event);
    if (handlers) {
      handlers.forEach(handler => handler(data));
    }
  }

  // Simulate real-time functionality for demo
  simulateRealTime() {
    // Simulate user count updates
    setInterval(() => {
      if (this.isConnected) {
        // Get the actual count from connected devices
        const count = Math.max(1, this.connectedDevices.size);
        this.emit('user_count_update', { count: count });
      }
    }, 5000);

    // Simulate some random devices for testing
    if (process.env.NODE_ENV !== 'production') {
      setTimeout(() => {
        if (this.isConnected && this.currentRoom && this.currentChannel) {
          // Add a fake device
          const fakeDeviceId = `Demo-${Math.floor(Math.random() * 1000)}`;
          const fakeIp = `192.168.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`;

          this.currentChannel.send({
            type: 'broadcast',
            event: 'user_joined',
            payload: {
              deviceId: fakeDeviceId,
              ip: fakeIp,
              timestamp: new Date().toISOString()
            }
          });
        }
      }, 3000);
    }
  }

  getSocket() {
    return this.socket;
  }

  getConnectionStatus() {
    return this.isConnected;
  }

  getCurrentRoom() {
    return this.currentRoom;
  }

  getDeviceId() {
    return this.deviceId;
  }

  getDeviceIp() {
    return this.deviceIp;
  }

  // Get connected devices
  getConnectedDevices() {
    return Array.from(this.connectedDevices.values());
  }

  // Get clipboard items for current room
  getClipboardItems() {
    if (!this.currentRoom) return [];
    return this.clipboardItems.get(this.currentRoom) || [];
  }
}

// Create a singleton instance
export const socketService = new SocketService();